import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  List,
  Checkbox,
  FormControlLabel,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Radio,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import TextInput from "../../../Components/InputComponents/TextInput";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import { decryptId } from "../../../Components/CryptoComponent";

export default function CloneRecruitmentProcedure() {
  const navigate = useNavigate();
  const { id } = useParams(); // this is the encrypted id
  const decryptedId = decryptId(decodeURIComponent(id));
  const [searchParams] = useSearchParams();

  const [initialProcedureName, setInitialProcedureName] = useState("");
  const [procedureName, setProcedureName] = useState("");
  const [processName, setProcessName] = useState("");
  const [processes, setProcesses] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editSubIndex, setEditSubIndex] = useState(null);
  const [editData, setEditData] = useState({ RSP_Name: "", fields: [] });

  const [openSubtabDialog, setOpenSubtabDialog] = React.useState(false);
  const [subtabName, setSubtabName] = React.useState("");
  const [subtabParentIndex, setSubtabParentIndex] = React.useState(null);

  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [expandAll, setExpandAll] = useState(false);

  const handleToggleAll = () => {
    const newState = {};
    processes.forEach((_, i) => {
      newState[i] = !expandAll;
    });
    setExpandedAccordions(newState);
    setExpandAll(!expandAll);
  };

  const handleOpenSubtabDialog = (index) => {
    setSubtabParentIndex(index);
    setSubtabName("");
    setOpenSubtabDialog(true);
  };

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);

  const handleRenameProcess = (i) => {
    setRenameValue(processes[i].RPS_Name);
    setRenameTarget({ i, isSub: false });
    setRenameDialogOpen(true);
  };

  const handleRenameSubprocess = (i, j) => {
    setRenameValue(processes[i].subProcedures[j].RSP_Name);
    setRenameTarget({ i, j, isSub: true });
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = () => {
    if (!renameTarget || !renameValue.trim()) {
      if (renameTarget?.isSub) {
        infoNotify("Please enter a Sub Tab Name");
      }
      if (!renameTarget?.isSub) {
        infoNotify("Please enter a Process Name");
      }

      return;
    }

    const updatedName = renameValue.trim().toLowerCase();
    const updated = [...processes];

    let isDuplicate = false;

    if (renameTarget.isSub) {
      const subProcs = updated[renameTarget.i]?.subProcedures || [];

      isDuplicate = subProcs.some(
        (sub, index) =>
          index !== renameTarget.j &&
          sub?.RSP_Name?.trim().toLowerCase() === updatedName,
      );

      if (isDuplicate) {
        infoNotify("Sub Tab name already exists.");
        return;
      }

      updated[renameTarget.i].subProcedures[renameTarget.j].RSP_Name =
        renameValue.trim();
    } else {
      isDuplicate = updated.some(
        (proc, index) =>
          index !== renameTarget.i &&
          proc?.RPS_Name?.trim().toLowerCase() === updatedName,
      );

      if (isDuplicate) {
        infoNotify("Process name already exists.");
        return;
      }

      updated[renameTarget.i].RPS_Name = renameValue.trim();
    }

    setProcesses(updated);
    setRenameDialogOpen(false);
  };

  const handleConfirmAddSubtab = () => {
    if (subtabName.trim() === "") {
      infoNotify("Please enter a Sub Tab Name");

      return;
    } // optional validation

    const isDuplicate = processes[subtabParentIndex]?.subProcedures?.some(
      (subProc) =>
        subProc?.RSP_Name?.trim().toLowerCase() ==
        subtabName.trim().toLowerCase(),
    );

    if (isDuplicate) {
      infoNotify("Sub Tab already exists.");
    } else {
      const updated = [...processes];
      updated[subtabParentIndex].subProcedures.push({
        RSP_Name: subtabName,
        fields: [],
      });
      setProcesses(updated);
      setOpenSubtabDialog(false);
      setSubtabName("");
      setSubtabParentIndex(null);
    }
  };

  const handleAddProcess = () => {
    if (!processName.trim()) {
      infoNotify("Please enter a Process Name");
      return;
    }

    const isDuplicate = processes.some(
      (proc) =>
        proc.RPS_Name.trim().toLowerCase() == processName.trim().toLowerCase(),
    );

    if (isDuplicate) {
      infoNotify("Process name already exists.");
    } else {
      setProcesses([
        ...processes,
        { RPS_Name: processName.trim(), fields: [], subProcedures: [] },
      ]);
      setProcessName("");
    }
  };

  const handleOpenModal = (index, subIndex = null) => {
    const target =
      subIndex === null
        ? processes[index]
        : processes[index].subProcedures[subIndex];
    setEditIndex(index);
    setEditSubIndex(subIndex);
    setEditData({
      RPS_Name: target.name,
      fields: target.fields?.length
        ? target.fields
        : [
          {
            RPF_Name: "",
            RPF_Type: "ShortText",
            RPF_IsRequired: 0,
            RPF_MaxLength: "",
            RPF_IsExpiry: 0,
            RPF_ExpiryPeriod: null,
            RPF_ValidValues: null,
            RPF_Value: "",
            RPF_IsViewable: 0,
          },
        ],
    });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    // Check for any empty field names
    const hasEmptyName = editData.fields.some(
      (field) => field.RPF_Name.trim() === "",
    );

    if (hasEmptyName) {
      infoNotify("Can't add Fields with an empty values.");
      return;
    }

    const hasInvalidOptions = editData.fields.some((field) => {
      const typesNeedingOptions = ["Checkbox", "Radio", "Dropdown"];
      if (typesNeedingOptions.includes(field.RPF_Type)) {
        return (
          !Array.isArray(field.RPF_ValidValues) ||
          field.RPF_ValidValues.length == 0 ||
          field.RPF_ValidValues.some((val) => val.trim() === "")
        );
      }
      return false;
    });

    if (hasInvalidOptions) {
      infoNotify(
        "Checkbox, Radio, and Dropdown fields must have non-empty options.",
      );
      return;
    }

    // Proceed to save
    const updated = [...processes];
    const target =
      editSubIndex === null
        ? updated[editIndex]
        : updated[editIndex].subProcedures[editSubIndex];

    target.fields = editData.fields.map((field) => ({
      ...field,
      RPF_Type: field.RPF_Type?.trim() === "" ? "ShortText" : field.RPF_Type,
    }));

    setProcesses(updated);
    setIsModalOpen(false);
    setEditIndex(null);
    setEditSubIndex(null);
    setEditData({ RPS_Name: "", fields: [] });
  };

  const handleProcedureSave = () => {
    const normalizeValidValues = (value) => {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : null;
        } catch {
          return null;
        }
      }
      return value;
    };

    if (initialProcedureName == procedureName) {
      infoNotify(
        "Recruitment procedure already exists. Please change the Procedure Name!",
      );
    } else {
      const procedureData = {
        RP_Name: procedureName,
        stages: processes?.map((proc) => ({
          ...proc,
          fields: proc.fields?.map((f) => ({
            ...f,
            RPF_ValidValues: normalizeValidValues(f.RPF_ValidValues),
          })),
          subProcedures: proc.subProcedures?.map((sub) => ({
            ...sub,
            fields: sub.fields?.map((f) => ({
              ...f,
              RPF_ValidValues: normalizeValidValues(f.RPF_ValidValues),
            })),
          })),
        })),
      };

      const validateFields = (procedureData) => {
        if (!procedureData?.stages?.length) {
          return { isValid: false, message: "No stages Added." };
        }

        for (const stage of procedureData.stages) {
          const hasSubProcedures =
            Array.isArray(stage.subProcedures) &&
            stage.subProcedures.length > 0;

          if (hasSubProcedures) {
            for (const sub of stage.subProcedures) {
              const validFields = Array.isArray(sub.fields)
                ? sub.fields.filter(
                  (f) =>
                    f.RPF_Type !== "IsCompleted" || f.RPF_Type !== "misc",
                )
                : [];

              if (validFields.length === 0) {
                return {
                  isValid: false,
                  message: `Please add at least one field for Sub Process: ${sub.RSP_Name || "Unnamed Subprocess"
                    } in Progress: ${stage.RPS_Name || "Unnamed Stage"}`,
                };
              }
            }
          } else {
            const validFields = Array.isArray(stage.fields)
              ? stage.fields.filter(
                (f) => f.RPF_Type !== "IsCompleted" || f.RPF_Type !== "misc",
              )
              : [];

            if (validFields.length === 0) {
              return {
                isValid: false,
                message: `Please add at least one field for Process: ${stage.RPS_Name || "Unnamed Stage"
                  }`,
              };
            }
          }
        }

        return { isValid: true };
      };

      const validation = validateFields(procedureData);

      if (validation.isValid) {
        const addIsCompletedField = (data) => {
          const cloned = JSON.parse(JSON.stringify(data)); // deep clone to avoid mutating original

          cloned.stages.forEach((stage) => {
            const hasFields =
              Array.isArray(stage.fields) && stage.fields.length > 0;
            const hasSubProcedures =
              Array.isArray(stage.subProcedures) &&
              stage.subProcedures.length > 0;

            // Case 1: Stage has fields but no subProcedures
            if (hasFields && !hasSubProcedures) {
              stage.fields.push(
                {
                  RPF_Name: "Process Completed?",
                  RPF_Type: "IsCompleted",
                  RPF_IsRequired: 0,
                  RPF_MaxLength: "",
                  RPF_ValidValues: null,
                  RPF_Value: 4,
                  RPF_IsExpiry: 0,
                  RPF_ExpiryPeriod: null,
                  RPF_ValidValues: null,
                  RPF_IsViewable: 0,
                },
                {
                  RPF_Name: "Completed Date",
                  RPF_Type: "misc",
                  RPF_IsRequired: 0,
                  RPF_MaxLength: "",
                  RPF_ValidValues: null,
                  RPF_Value: "",
                  RPF_IsViewable: 0,
                  RPF_IsExpiry: 0,
                  RPF_ExpiryPeriod: null,
                  RPF_ValidValues: null,
                },
                {
                  RPF_Name: "User",
                  RPF_Type: "misc",
                  RPF_IsRequired: 0,
                  RPF_MaxLength: "",
                  RPF_ValidValues: null,
                  RPF_Value: "",
                  RPF_IsViewable: 0,
                  RPF_IsExpiry: 0,
                  RPF_ExpiryPeriod: null,
                  RPF_ValidValues: null,
                },
              );
            }

            // Case 2: Stage has subProcedures but no fields
            if (!hasFields && hasSubProcedures) {
              stage.subProcedures.forEach((sub) => {
                const hasSubFields =
                  Array.isArray(sub.fields) && sub.fields.length > 0;
                if (hasSubFields) {
                  sub.fields.push(
                    {
                      RPF_Name: "Process Completed?",
                      RPF_Type: "IsCompleted",
                      RPF_IsRequired: 0,
                      RPF_MaxLength: "",
                      RPF_ValidValues: null,
                      RPF_Value: 4,
                      RPF_IsExpiry: 0,
                      RPF_ExpiryPeriod: null,
                      RPF_ValidValues: null,
                      RPF_IsViewable: 0,
                    },
                    {
                      RPF_Name: "Completed Date",
                      RPF_Type: "misc",
                      RPF_IsRequired: 0,
                      RPF_MaxLength: "",
                      RPF_ValidValues: null,
                      RPF_Value: "",
                      RPF_IsViewable: 0,
                      RPF_IsExpiry: 0,
                      RPF_ExpiryPeriod: null,
                      RPF_ValidValues: null,
                    },
                    {
                      RPF_Name: "User",
                      RPF_Type: "misc",
                      RPF_IsRequired: 0,
                      RPF_MaxLength: "",
                      RPF_ValidValues: null,
                      RPF_Value: "",
                      RPF_IsViewable: 0,
                      RPF_IsExpiry: 0,
                      RPF_ExpiryPeriod: null,
                      RPF_ValidValues: null,
                    },
                  );
                }
              });
            }
          });

          return cloned;
        };

        axiosAPI
          ?.post(
            `/RecruitmentProcedure/add`,
            addIsCompletedField(procedureData),
          )
          .then((res) => {
            if (res?.data?.success) {
              successNotify("Recruitment Process Cloned Successfully!");
              navigate(`/RecruitmentProcedure?${searchParams?.toString()}`);
            } else {
              errorNotify("Something went Wrong!");
            }
          })
          .catch((err) => {
            errorNotify("Something went Wrong!");
          });
      } else {
        infoNotify(validation.message);
      }
    }
  };

  const handleFieldChange = (idx, key, value) => {
    const updatedFields = [...editData.fields];
    updatedFields[idx][key] = value;
    setEditData({ ...editData, fields: updatedFields });
  };

  const handleAddField = () => {
    setEditData({
      ...editData,
      fields: [
        ...editData.fields,
        {
          RPF_Name: "",
          RPF_Type: "ShortText",
          RPF_IsRequired: 0,
          RPF_MaxLength: "",
          RPF_IsExpiry: 0,
          RPF_ExpiryPeriod: null,
          RPF_ValidValues: null,
          RPF_Value: "",
          RPF_IsViewable: 0,
        },
      ],
    });
  };

  const handleDeleteField = (idx) => {
    const updated = [...editData.fields];
    updated.splice(idx, 1);
    setEditData({ ...editData, fields: updated });
  };

  const handleDeleteProcess = (index) => {
    const updated = [...processes];
    updated.splice(index, 1);
    setProcesses(updated);
  };

  const handleDeleteSubprocess = (processIndex, subIndex) => {
    const updated = [...processes];
    updated[processIndex].subProcedures.splice(subIndex, 1);
    setProcesses(updated);
  };

  const handleSelectOptionChange = (fieldIdx, optIdx, value) => {
    const updatedFields = [...editData.fields];
    const currentValues = updatedFields[fieldIdx]?.RPF_ValidValues;

    let parsedValues = [];

    if (Array.isArray(currentValues)) {
      parsedValues = [...currentValues];
    } else if (typeof currentValues === "string") {
      try {
        const parsed = JSON.parse(currentValues);
        parsedValues = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Failed to parse RPF_ValidValues:", e);
        parsedValues = [];
      }
    }

    parsedValues[optIdx] = value;
    updatedFields[fieldIdx].RPF_ValidValues = parsedValues;

    setEditData({ ...editData, fields: updatedFields });
  };

  const addSelectOption = (fieldIdx) => {
    const updatedFields = [...editData.fields];
    const currentValues = updatedFields[fieldIdx]?.RPF_ValidValues;

    let parsedValues = [];

    if (Array.isArray(currentValues)) {
      parsedValues = [...currentValues];
    } else if (typeof currentValues === "string") {
      try {
        const parsed = JSON.parse(currentValues);
        parsedValues = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Failed to parse RPF_ValidValues:", e);
        parsedValues = [];
      }
    }

    parsedValues.push(""); // Add empty string for new option
    updatedFields[fieldIdx].RPF_ValidValues = parsedValues;

    setEditData({ ...editData, fields: updatedFields });
  };

  const removeSelectOption = (fieldIdx, optIdx) => {
    const updatedFields = [...editData.fields];
    const currentValues = updatedFields[fieldIdx]?.RPF_ValidValues;

    let parsedValues = [];

    if (Array.isArray(currentValues)) {
      parsedValues = [...currentValues];
    } else if (typeof currentValues === "string") {
      try {
        const parsed = JSON.parse(currentValues);
        parsedValues = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Failed to parse RPF_ValidValues:", e);
        parsedValues = [];
      }
    }

    parsedValues.splice(optIdx, 1);
    updatedFields[fieldIdx].RPF_ValidValues = parsedValues;

    setEditData({ ...editData, fields: updatedFields });
  };

  // state to track selection and processes
  const [selectedProcessIndex, setSelectedProcessIndex] = React.useState(null);

  // Move functions
  const moveProcessUp = () => {
    if (selectedProcessIndex === null || selectedProcessIndex === 0) return;
    setProcesses((prev) => {
      const newProcesses = [...prev];
      [
        newProcesses[selectedProcessIndex - 1],
        newProcesses[selectedProcessIndex],
      ] = [
          newProcesses[selectedProcessIndex],
          newProcesses[selectedProcessIndex - 1],
        ];
      return newProcesses;
    });
    setSelectedProcessIndex(selectedProcessIndex - 1);
  };

  const moveProcessDown = () => {
    if (
      selectedProcessIndex === null ||
      selectedProcessIndex === processes.length - 1
    )
      return;
    setProcesses((prev) => {
      const newProcesses = [...prev];
      [
        newProcesses[selectedProcessIndex + 1],
        newProcesses[selectedProcessIndex],
      ] = [
          newProcesses[selectedProcessIndex],
          newProcesses[selectedProcessIndex + 1],
        ];
      return newProcesses;
    });
    setSelectedProcessIndex(selectedProcessIndex + 1);
  };

  const getRecruitmentProcedure = async () => {
    try {
      const res = await axiosAPI.get(
        `/RecruitmentProcedure/listbyId/${decryptedId}`,
      );
      setInitialProcedureName(res?.data?.data?.RP_Name);
      setProcedureName(res?.data?.data?.RP_Name);
      setProcesses(res?.data?.data?.stages);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getRecruitmentProcedure();
  }, []);

  const typeOptions = [
    { label: "Short Text", value: "ShortText" },
    { label: "Long Text / Comments", value: "LongText" },
    { label: "Number", value: "Number" },
    { label: "Date", value: "Date" },
    { label: "Time", value: "Time" },
    { label: "Checkbox (Yes/No)", value: "Checkbox" },
    { label: "Radio", value: "Radio" },
    { label: "Dropdown with options", value: "Dropdown" },
    { label: "Attachment", value: "Attachment" },
    { label: "Completion Status", value: "IsCompleted" },
  ];

  const getTypeLabel = (type) => {
    const match = typeOptions.find(
      (opt) => opt.value.toLowerCase() === (type || "").toLowerCase(),
    );
    return match ? match.label : type; // fallback to original if not found
  };

  return (
    <>
      {isModalOpen ? (
        <Paper
          sx={{
            height: "100%", // full viewport height
            display: "flex",
            flexDirection: "column",
            boxShadow: "none",
            p: 2,
            fontFamily: "Geist, sans-serif",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Add {editSubIndex !== null ? "Sub Tab Fields" : "Tab Fields"}
            </Typography>
          </Box>

          {/* Scrollable content */}
          <Box
            sx={{
              flex: 1, // take remaining height between header & footer
              minHeight: 0, // IMPORTANT: lets this child actually shrink for overflow
              maxHeight: "80%",
              overflowY: "auto", // vertical scroll only
              overflowX: "hidden",
              pr: 1,
              mt: 1,
              // optional: add bottom padding so last field isn't obscured by sticky footer shadow
              pb: 2,
            }}
          >
            {editData.fields
              ?.filter(
                (field) =>
                  field?.RPF_Type !== "IsCompleted" &&
                  field?.RPF_Type !== "misc",
              )
              ?.map((field, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: "100%",
                    border: "1px solid lightgrey",
                    mt: 1.25,
                    p: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Field Name */}
                    <Grid item xs={12} sm={12} lg={4} xl={2.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Field Name
                      </Typography>
                      <TextInput
                        label="Field Name"
                        placeholder="Enter Field Name"
                        fullWidth
                        required
                        value={field?.RPF_Name}
                        onChange={(e) =>
                          handleFieldChange(idx, "RPF_Name", e.target.value)
                        }
                      />
                    </Grid>

                    {/* Type */}
                    <Grid item xs={12} sm={12} lg={4} xl={2.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Field Type
                      </Typography>
                      <SelectInput
                        value={field?.RPF_Type}
                        onChange={(e) =>
                          handleFieldChange(idx, "RPF_Type", e.target.value)
                        }
                        name="Type"
                        // dropdownMaxHeight={150}
                        // (optional) force menu to open upwards
                        MenuProps={{
                          anchorOrigin: { vertical: "top", horizontal: "left" },
                          transformOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                        }}
                        usePortal={false}
                        // Prefer opening upwards to reduce clipping near bottom:
                        // menuPlacement="top"
                        options={[
                          { label: "Short Text", value: "ShortText" },
                          { label: "Long Text / Comments", value: "LongText" },
                          { label: "Number", value: "Number" },
                          { label: "Date", value: "Date" },
                          { label: "Time", value: "Time" },
                          { label: "Checkbox (Yes/No)", value: "Checkbox" },
                          { label: "Radio", value: "Radio" },
                          { label: "Dropdown with options", value: "Dropdown" },
                          { label: "Attachment", value: "Attachment" },
                        ]}
                      />
                    </Grid>

                    {/* Max Length */}
                    {(field?.RPF_Type === "ShortText" ||
                      field?.RPF_Type === "LongText" ||
                      field?.RPF_Type === "Number") && (
                        <Grid item xs={12} sm={12} lg={4} xl={2.5}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500 }}
                          >
                            Max Length
                          </Typography>
                          <TextInput
                            label="Max Length"
                            placeholder="Enter Max Length"
                            fullWidth
                            value={field?.RPF_MaxLength}
                            allowOnlyNumbers
                            capAt={255}
                            onChange={(e) =>
                              handleFieldChange(
                                idx,
                                "RPF_MaxLength",
                                e.target.value,
                              )
                            }
                          />
                        </Grid>
                      )}

                    {/* Viewable for Candidate */}
                    {field?.RPF_Type === "Attachment" && (
                      <Grid item xs={12} lg={4} xl={2.5}>
                        <FormControlLabel
                          sx={{ mt: 3 }}
                          control={
                            <Checkbox
                              checked={!!field.RPF_IsViewable}
                              onChange={(e) =>
                                handleFieldChange(
                                  idx,
                                  "RPF_IsViewable",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                            />
                          }
                          label="Viewable for Candidate"
                        />
                      </Grid>
                    )}

                    {/* Expiry Period */}
                    {field?.RPF_Type === "Date" &&
                      field?.RPF_IsExpiry === 1 && (
                        <Grid item xs={12} lg={4} xl={2.5}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500 }}
                          >
                            Expiry Period
                          </Typography>
                          <TextInput
                            label="Days till Expiry"
                            placeholder="Enter Days till Expiry"
                            fullWidth
                            value={Number(field?.RPF_ExpiryPeriod) || ""}
                            allowOnlyNumbers
                            capAt={365}
                            onChange={(e) =>
                              handleFieldChange(
                                idx,
                                "RPF_ExpiryPeriod",
                                e.target.value,
                              )
                            }
                          />
                        </Grid>
                      )}

                    {/* Add Option */}
                    {(field?.RPF_Type === "Dropdown" ||
                      field?.RPF_Type === "Radio" ||
                      field?.RPF_Type === "Checkbox") && (
                        <Grid item xs={12} sm={3} lg={4} xl={1.5}>
                          <Button
                            sx={{
                              position: "relative",
                              top: { md: 0, lg: 30 },
                              border: "2px solid",
                              p: "5px 10px",
                            }}
                            size="small"
                            onClick={() => addSelectOption(idx)}
                          >
                            Add an Option
                          </Button>
                        </Grid>
                      )}

                    {/* Option Fields */}
                    {(field?.RPF_Type === "Dropdown" ||
                      field?.RPF_Type === "Radio" ||
                      field?.RPF_Type === "Checkbox") && (
                        <Grid container item xs={12} md={12}>
                          {Array.isArray(field?.RPF_ValidValues) &&
                            field.RPF_ValidValues.length > 0
                            ? field.RPF_ValidValues.map((option, optIdx) => (
                              <Grid
                                container
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={2.5}
                                key={optIdx}
                              >
                                <Grid item xs={10}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    Option {optIdx + 1}
                                  </Typography>
                                  <TextInput
                                    label="Option"
                                    placeholder="Enter Option"
                                    fullWidth
                                    value={option}
                                    onChange={(e) =>
                                      handleSelectOptionChange(
                                        idx,
                                        optIdx,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>
                                <Grid
                                  item
                                  xs={2}
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <IconButton
                                    onClick={() =>
                                      removeSelectOption(idx, optIdx)
                                    }
                                    sx={{ mt: { xs: 0, md: 3 } }}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            ))
                            : null}
                        </Grid>
                      )}

                    <Grid item xs={12}>
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        {/* Left side checkboxes */}
                        <Grid item>
                          <Grid container direction="column">
                            {/* Required */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!field.RPF_IsRequired}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      idx,
                                      "RPF_IsRequired",
                                      e.target.checked ? 1 : 0,
                                    )
                                  }
                                />
                              }
                              label="Required"
                            />

                            {/* Expiry Period */}
                            {field?.RPF_Type === "Date" && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={Number(field.RPF_IsExpiry)}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        idx,
                                        "RPF_IsExpiry",
                                        e.target.checked ? 1 : 0,
                                      )
                                    }
                                  />
                                }
                                label="Expiry Period?"
                              />
                            )}
                          </Grid>
                        </Grid>

                        {/* Right side delete button */}
                        <Grid item>
                          <IconButton
                            onClick={() => handleDeleteField(idx)}
                            color="error"
                          >
                            <DeleteIcon sx={{ fontSize: "32px" }} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              ))}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                my: 4,
              }}
            >
              <Button
                variant="outlined"
                customColor="#B0BEC5"
                onClick={handleAddField}
              >
                Add Field
              </Button>
            </Box>
          </Box>

          {/* Footer (always visible) */}
          <Box
            sx={{
              pt: 1,
              // optional sticky look + separation
              // position: "sticky",
              // bottom: 0,
              background: (theme) => theme.palette.background.paper,
            }}
          >
            {/* <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mb: 1,
              }}
            >
              <CommonButton
                variant="outlined"
                customColor="#B0BEC5"
                onClick={handleAddField}
              >
                Add Field
              </CommonButton>
            </Box> */}

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <CommonButton
                  variant="outlined"
                  customColor="#B0BEC5"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </CommonButton>
              </Grid>
              <Grid item>
                <CommonButton
                  variant="contained"
                  customColor="#007BFF"
                  onClick={handleSave}
                >
                  Save
                </CommonButton>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      ) : (
        <Paper
          sx={{
            maxWidth: `100vw`,
            padding: 2,
            margin: "auto",
            boxShadow: "none",
            overflowX: "auto",
            fontFamily: "Geist, sans-serif",
            minHeight: "100%",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
            mb={1}
          >
            Clone Recruitment Procedure
          </Typography>

          <Grid
            container
            sx={{
              border: "1px solid lightgrey",
              padding: "20px",
            }}
            mb={3}
          >
            <Grid item sx={{ padding: "5px" }} xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Procedure Name
              </Typography>
              <TextInput
                fullWidth
                label="Procedure Name"
                placeholder="Enter Procedure Name"
                value={procedureName}
                onChange={(e) => setProcedureName(e.target.value)}
              />
            </Grid>

            <Grid item sx={{ padding: "5px" }} xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Process Name
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }} gap={2}>
                <TextInput
                  fullWidth
                  label="Process Name"
                  placeholder="Enter Process Name"
                  value={processName}
                  onChange={(e) => setProcessName(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddProcess}
                  sx={{ width: "200px" }}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>

          {processes?.length !== 0 && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="outlined" onClick={handleToggleAll}>
                {expandAll ? (
                  <>
                    <ExpandLessIcon sx={{ mr: 1 }} />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ExpandMoreIcon sx={{ mr: 1 }} />
                    Expand All
                  </>
                )}
              </Button>
            </Box>
          )}
          {processes?.length > 1 ? (
            <Box mb={2} display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={moveProcessUp}
                disabled={
                  selectedProcessIndex === null || selectedProcessIndex === 0
                }
              >
                <ArrowUpwardIcon />
              </Button>
              <Button
                variant="outlined"
                onClick={moveProcessDown}
                disabled={
                  selectedProcessIndex === null ||
                  selectedProcessIndex === processes.length - 1
                }
              >
                <ArrowDownwardIcon />
              </Button>
            </Box>
          ) : null}

          <List>
            {processes?.map((proc, i) => (
              <>
                <Grid container>
                  {processes?.length > 1 ? (
                    <Grid item xs={3} sm={2} md={0.5}>
                      <Checkbox
                        checked={selectedProcessIndex == i}
                        onChange={() => {
                          setSelectedProcessIndex(i);
                        }}
                      />
                    </Grid>
                  ) : null}

                  <Grid
                    item
                    xs={processes?.length > 1 ? 9 : 12}
                    sm={processes?.length > 1 ? 10 : 12}
                    md={processes?.length > 1 ? 11.5 : 12}
                  >
                    <Accordion
                      expanded={!!expandedAccordions[i]}
                      onChange={() => {
                        setExpandedAccordions((prev) => ({
                          ...prev,
                          [i]: !prev[i],
                        }));
                      }}
                      key={i}
                      sx={{
                        mb: 2,
                        border:
                          i === selectedProcessIndex
                            ? "2px solid #1976d2"
                            : "1px solid lightgrey",
                        boxShadow: "none",
                        cursor: "pointer",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {`${i + 1}) ${proc.RPS_Name}`}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {/* Process Actions */}
                          <Grid item xs={12}>
                            <Grid
                              container
                              justifyContent="flex-end"
                              spacing={1}
                            >
                              {proc.fields.length === 0 && (
                                <>
                                  <Grid item>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={() => handleOpenSubtabDialog(i)}
                                    >
                                      Add Sub Tab
                                    </Button>
                                  </Grid>
                                  {proc.subProcedures?.length != 0 ? (
                                    <Grid item>
                                      <Tooltip
                                        PopperProps={{
                                          disablePortal: true,
                                        }}
                                        title="Rename the Process"
                                      >
                                        <IconButton
                                          onClick={() => handleRenameProcess(i)}
                                        >
                                          <EditOutlinedIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Grid>
                                  ) : null}
                                </>
                              )}
                              {proc.subProcedures?.length === 0 && (
                                <>
                                  <Grid item>
                                    <Tooltip
                                      PopperProps={{
                                        disablePortal: true,
                                      }}
                                      title="Rename the Process"
                                    >
                                      <IconButton
                                        onClick={() => handleRenameProcess(i)}
                                      >
                                        <EditOutlinedIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                  <Grid item>
                                    <Tooltip
                                      PopperProps={{
                                        disablePortal: true,
                                      }}
                                      title="Add Fields for the process"
                                    >
                                      <IconButton
                                        onClick={() => handleOpenModal(i)}
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                  <Grid item>
                                    <IconButton
                                      onClick={() => handleDeleteProcess(i)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </>
                              )}
                            </Grid>
                          </Grid>

                          {/* Fields Table */}
                          {proc.fields.length > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ overflowX: "auto" }}>
                                <Table sx={{ minWidth: 600 }}>
                                  <TableHead>
                                    <TableRow>
                                      {[
                                        "Field Name",
                                        "Field Type",
                                        "Required",
                                        "Max Length",
                                        "Select Options",
                                        "Expiry Period",
                                      ]?.map((head, idx) => (
                                        <TableCell
                                          key={idx}
                                          sx={{
                                            fontWeight: 500,
                                            fontFamily: "Geist, sans-serif",
                                          }}
                                        >
                                          {head}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {proc.fields
                                      ?.filter(
                                        (f) =>
                                          f?.RPF_Type != "IsCompleted" &&
                                          f?.RPF_Type != "misc",
                                      )
                                      ?.map((f, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{f?.RPF_Name}</TableCell>
                                          <TableCell>
                                            {getTypeLabel(f?.RPF_Type)}{" "}
                                          </TableCell>
                                          <TableCell>
                                            {f?.RPF_IsRequired ? "Yes" : "No"}
                                          </TableCell>
                                          <TableCell>
                                            {f.RPF_MaxLength || "-"}
                                          </TableCell>

                                          <TableCell>
                                            {(f?.RPF_Type === "Radio" ||
                                              f?.RPF_Type === "Dropdown" ||
                                              f?.RPF_Type === "Checkbox") &&
                                              (() => {
                                                let validValues =
                                                  f.RPF_ValidValues;

                                                // If it's a string, try to parse it
                                                if (
                                                  typeof validValues ===
                                                  "string"
                                                ) {
                                                  try {
                                                    validValues =
                                                      JSON.parse(validValues);
                                                  } catch (err) {
                                                    console.error(
                                                      "Invalid JSON in RPF_ValidValues:",
                                                      err,
                                                    );
                                                    validValues = [];
                                                  }
                                                }

                                                // Check if it's a valid array
                                                if (
                                                  Array.isArray(validValues) &&
                                                  validValues.length > 0
                                                ) {
                                                  return validValues?.map(
                                                    (opt, i) => (
                                                      // <Box key={i}>{opt}</Box>
                                                      <Chip
                                                        label={opt}
                                                        sx={{ m: 0.5 }}
                                                      />
                                                    ),
                                                  );
                                                }

                                                // Empty array or invalid
                                                return "-";
                                              })()}
                                          </TableCell>
                                          <TableCell>
                                            {f.RPF_ExpiryPeriod &&
                                              !isNaN(
                                                Number(
                                                  f.RPF_ExpiryPeriod.toString().replace(
                                                    /"/g,
                                                    "",
                                                  ),
                                                ),
                                              )
                                              ? `${f.RPF_ExpiryPeriod.toString().replace(
                                                /"/g,
                                                "",
                                              )} Days`
                                              : "-"}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Grid>
                          )}

                          {/* Subprocess Accordions */}
                          {proc.subProcedures?.map((subProcedures, j) => (
                            <Grid item xs={12} key={j}>
                              <Accordion
                                key={j}
                                sx={{
                                  border: "1px solid lightgrey",
                                  boxShadow: "none",
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontFamily: "Geist, sans-serif",
                                    }}
                                  >
                                    {`${i + 1}.${j + 1}) ${subProcedures?.RSP_Name
                                      }`}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Grid
                                        container
                                        justifyContent="flex-end"
                                        spacing={1}
                                      >
                                        <Grid item>
                                          <Tooltip
                                            PopperProps={{
                                              disablePortal: true,
                                            }}
                                            title="Rename the Sub process"
                                          >
                                            <IconButton
                                              onClick={() =>
                                                handleRenameSubprocess(i, j)
                                              }
                                            >
                                              <EditOutlinedIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Grid>
                                        <Grid item>
                                          <Tooltip
                                            PopperProps={{
                                              disablePortal: true,
                                            }}
                                            title="Add Fields for the Sub process"
                                          >
                                            <IconButton
                                              onClick={() =>
                                                handleOpenModal(i, j)
                                              }
                                            >
                                              <AddIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Grid>
                                        <Grid item>
                                          <IconButton
                                            onClick={() =>
                                              handleDeleteSubprocess(i, j)
                                            }
                                            color="error"
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    </Grid>

                                    {subProcedures.fields.length > 0 && (
                                      <Grid item xs={12}>
                                        <Box sx={{ overflowX: "auto" }}>
                                          <Table sx={{ minWidth: 600 }}>
                                            <TableHead>
                                              <TableRow>
                                                {[
                                                  "Field Name",
                                                  "Field Type",
                                                  "Required",
                                                  "Max Length",
                                                  "Select Options",
                                                  "Expiry Period",
                                                ]?.map((head, idx) => (
                                                  <TableCell
                                                    key={idx}
                                                    sx={{ fontWeight: 500 }}
                                                  >
                                                    {head}
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {subProcedures.fields
                                                ?.filter(
                                                  (f) =>
                                                    f?.RPF_Type !=
                                                    "IsCompleted" &&
                                                    f?.RPF_Type != "misc",
                                                )
                                                ?.map((f, idx) => (
                                                  <TableRow key={idx}>
                                                    <TableCell>
                                                      {f?.RPF_Name}
                                                    </TableCell>
                                                    <TableCell>
                                                      {getTypeLabel(
                                                        f?.RPF_Type,
                                                      )}
                                                    </TableCell>
                                                    <TableCell>
                                                      {f.RPF_IsRequired
                                                        ? "Yes"
                                                        : "No"}
                                                    </TableCell>
                                                    <TableCell>
                                                      {f?.RPF_MaxLength || "-"}
                                                    </TableCell>
                                                    {/* <TableCell>
                                                  {f?.expiryDate ? "Yes" : "No"}
                                                </TableCell> */}
                                                    <TableCell>
                                                      {(f?.RPF_Type ===
                                                        "Radio" ||
                                                        f?.RPF_Type ===
                                                        "Dropdown" ||
                                                        f?.RPF_Type ===
                                                        "Checkbox") &&
                                                        (() => {
                                                          let validValues =
                                                            f.RPF_ValidValues;

                                                          if (
                                                            typeof validValues ===
                                                            "string"
                                                          ) {
                                                            try {
                                                              validValues =
                                                                JSON.parse(
                                                                  validValues,
                                                                );
                                                            } catch (error) {
                                                              console.error(
                                                                "Invalid JSON in RPF_ValidValues:",
                                                                error,
                                                              );
                                                              validValues = [];
                                                            }
                                                          }

                                                          return Array.isArray(
                                                            validValues,
                                                          )
                                                            ? validValues.length >
                                                              0
                                                              ? validValues?.map(
                                                                (opt, i) => (
                                                                  <Box
                                                                    key={i}
                                                                  >
                                                                    {opt}
                                                                  </Box>
                                                                ),
                                                              )
                                                              : "-"
                                                            : null;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                      {f.RPF_ExpiryPeriod &&
                                                        !isNaN(
                                                          Number(
                                                            f.RPF_ExpiryPeriod.toString().replace(
                                                              /"/g,
                                                              "",
                                                            ),
                                                          ),
                                                        )
                                                        ? `${f.RPF_ExpiryPeriod.toString().replace(
                                                          /"/g,
                                                          "",
                                                        )} Days`
                                                        : "-"}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Grid>
                                    )}
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </>
            ))}
          </List>

          <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Grid item>
              <CommonButton
                variant="outlined"
                customColor="#B0BEC5"
                onClick={() => {
                  navigate(`/RecruitmentProcedure?${searchParams?.toString()}`);
                }}
              >
                Cancel
              </CommonButton>
            </Grid>
            <Grid item>
              <CommonButton
                variant="contained"
                customColor="#007BFF"
                onClick={handleProcedureSave}
              >
                Save
              </CommonButton>
            </Grid>
          </Grid>

          <Dialog
            open={openSubtabDialog}
            onClose={() => setOpenSubtabDialog(false)}
          >
            <DialogTitle>Add Subtab Name</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Subtab Name"
                fullWidth
                value={subtabName}
                onChange={(e) => setSubtabName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenSubtabDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleConfirmAddSubtab}
                color="primary"
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={renameDialogOpen}
            onClose={() => setRenameDialogOpen(false)}
          >
            <DialogTitle>
              Rename {renameTarget?.isSub ? "Subtab" : "Tab"}
            </DialogTitle>
            <DialogContent>
              <TextInput
                autoFocus
                margin="dense"
                label="New Name"
                fullWidth
                variant="outlined"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRenameConfirm} variant="contained">
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}
    </>
  );
}
